import React from "react";
import { Provider } from "react-redux";
import forEach from "lodash/forEach";
import initReactFastclick from "react-fastclick";
import XHR from "i18next-xhr-backend";
import Backend from "i18next-chained-backend";
import LocalStorageBackend from "i18next-localstorage-backend";
import i18n from "./src/i18n";
import { withPrefix } from "gatsby";
import wrapPage from "./wrap-page";
import { CacheProvider } from "@emotion/react";
import SmartPersistGate from "./src/components/SmartPersistGate";

// window.isNativeApp = true;

const unregisterSW = () => {
  if (
    navigator &&
    navigator.serviceWorker &&
    navigator.serviceWorker.getRegistrations
  ) {
    navigator.serviceWorker
      .getRegistrations()
      .then((registrations) =>
        forEach(registrations, (registration) => registration.unregister()),
      );
  }
};

unregisterSW();

import configureStore from "./src/store/configureStore";
import { createMuiCache } from "./src/components/AppContainer/theme-cache";
import { getParams } from "./src/utils/location";
import {
  initClarityHydrationMismatchDetector,
  trackClarityHydrationComplete,
} from "./src/layouts/clarityInfo";

const initialState = {};

export const wrapRootElement = ({ element }) => {
  const { store, persistor } = configureStore(initialState, { loadLocalStorage: true });
  const muiCache = createMuiCache();
  
  const ConnectedRootElement = (
    <CacheProvider value={muiCache}>
      <Provider store={store}>
        <SmartPersistGate persistor={persistor}>
          {element}
        </SmartPersistGate>
      </Provider>
    </CacheProvider>
  );
  return ConnectedRootElement;
};

export const onInitialClientRender = () => {
  trackClarityHydrationComplete();
};

export const onClientEntry = async () => {
  initClarityHydrationMismatchDetector();
  i18n.use(Backend).init({
    backend: {
      backends: [
        LocalStorageBackend, // primary
        XHR, // fallback
      ],
      backendOptions: [
        {
          expirationTime: 5 * 60 * 1000,
          prefix: `${process.env.GATSBY_BUSINESS_ID}_`,
        },
        { loadPath: withPrefix("locales/{{lng}}/{{ns}}.json") },
      ],
    },
  });
  initReactFastclick();

  if (typeof IntersectionObserver === `undefined`) {
    await import(`intersection-observer`);
  }

  try {
    const res = await fetch(withPrefix('shared-data.json'));
    window.__sharedData = await res.json();
  } catch (e) {
    console.error('[extractSharedContext] Failed to load shared-data.json:', e);
    window.__sharedData = null;
  }
};

export const wrapPageElement = ({ element, props }) => {
  if (
    props.pageContext.__sharedContextExtracted &&
    typeof window !== 'undefined' &&
    window.__sharedData
  ) {
    const enrichedPageContext = {
      ...props.pageContext,
      branches: window.__sharedData.branches || [],
      servingOptions: window.__sharedData.servingOptions || [],
      menuMultipliers: window.__sharedData.menuMultipliers || [],
      businessAppConfiguration: window.__sharedData.businessAppConfiguration || {},
    };
    const enrichedProps = { ...props, pageContext: enrichedPageContext };
    return wrapPage({ element: React.cloneElement(element, enrichedProps), props: enrichedProps });
  }
  return wrapPage({ element, props });
};

export const shouldUpdateScroll = ({
  routerProps: {
    location,
    location: { pathname },
  },
}) => {
  // should not scroll to top on order page in order to support scroll actions on page
  if (pathname.includes("order") && !getParams(location).checkoutOpen) {
    console.log("no scroll top");
    return false;
  }
  // default scroll behavior
  return true;
};
